// Ported from Octo.Mobile@79a7c69 src/types/system.ts
// System metadata types matching Octo.Models (camelCase to match API JSON responses)

export interface SystemScreen {
  screenId: string;
  screenName: string;
  screenCaption: string;
  parentScreenId: string | null;
  objectId: string | null;
  screenTypeId: string | null;
  screenTypeName: string | null;
  iconName: string | null;
  displayOrder: number;
  active: boolean;
  whereClause: string | null;
  iconId: string | null;
  children?: SystemScreen[];
}

export interface SystemObject {
  objectId: string;
  /** Bölünmüş tablo zincirinde üst nesne (SP @BaseObjectId parametresi bunu alır) */
  baseObjectId?: string | null;
  objectName: string;
  objectAlias: string | null;
  tableName: string;
  caption: string;
  keyFieldName: string;
  displayFieldName: string | null;
  splitTableName: string | null;
  splitKeyFieldName: string | null;
  maxRecords: number | null;
  isReadOnly: boolean;
  readOnly: boolean;
  whereClause: string | null;
  orderClause: string | null;
  orderByClause: string | null;
  beforeSPObjectId: string | null;
  timeOut: number | null;
  htmlCardTemplateId: string | null;
  htmlSummaryTemplateId: string | null;
  htmlSummaryObjectId: string | null;
  razorTemplateId: string | null;
  objectTypeId: string;
  scanBarcodeObjectId: string | null;
  // --- Web (Blazor SystemObject) alanları ---
  editModeId?: string | null;
  componentTypeId?: string | null;
  requeryAfterChange?: boolean;
  infoMessage?: string | null;
  deleteMessage?: string | null;
  documentUrl?: string | null;
  splitterPercentage?: number | null;
  editFormWidth?: string | null;
  /** Form düzeni (JSON, #489) — bkz. features/editform/layout/formSettings. */
  editFormSettings?: string | null;
  calendarEnabled?: boolean;
  /** Grid verisi dilim dilim okunsun mu (sonsuz kaydırma). Bkz. useGridPagedData. */
  pagedLoading?: boolean;
  dataCategoryId?: string | null;
}

export interface SystemHtmlCardTemplate {
  htmlCardTemplateId: string;
  htmlCardTemplateCode: string;
  htmlCardTemplate: string; // Raw HTML string
}

export interface SystemHtmlSummaryTemplate {
  htmlSummaryTemplateId: string;
  htmlSummaryTemplateCode: string;
  htmlSummaryTemplate: string; // Raw HTML string
}

export interface SystemRazorTemplate {
  razorTemplateId: string;
  razorTemplateCode: string;
  razorTemplate: string; // Raw Razor/HTML string
}

export interface SystemObjectFieldDbType {
  objectFieldDbTypeId: string;
  dbTypeName: string;
  baseType: string;
}

export interface SystemObjectField {
  objectFieldId: string;
  objectId: string;
  fieldName: string;
  baseFieldName: string | null;
  primaryKey: boolean;
  displayOrder: number;
  fieldDbTypeId: string;
  fieldDbTypeName: string | null;
  fieldDbType?: SystemObjectFieldDbType | null;
  editorTypeId: string;
  editorTypeName: string | null;
  editorType?: { editorTypeId: string; editorTypeName: string } | null;
  fieldCaption: string;
  width: number;
  length: number;
  scale: number | null;
  readOnly: boolean;
  visible: boolean;
  required: boolean;
  defaultValue: string | null;
  displayFormat: string | null;
  groupName: string | null;
  // Lookup
  lookupDataSourceObjectId: string | null;
  lookupValueFieldName: string | null;
  lookupTextFieldName: string | null;
  lookupPopupFieldNames: string | null;
  lookupWhereClause: string | null;
  lookupOrderClause: string | null;
  /** Yazılabilir lookup (#514): listede olmayan değer de yazılabilir, metin olduğu gibi kaydedilir. */
  lookupAllowCustomValue?: boolean | null;
  /** Lookup erişim modu (Both / DropDownOnly / LibraryOnly) kimliği */
  lookupAccessModeId?: string | null;
  /** Açılır liste açılabilir mi? LibraryOnly modda false gelir. */
  showLookupDropDown?: boolean | null;
  /** "..." kütüphane düğmesi gösterilsin mi? (DropDownOnly modda false) */
  showLookupLibraryButton?: boolean | null;
  // Calculated
  calculatedExpression: string | null;
  isCalculated: boolean;
  // Auto code
  autoCodePrefix: string | null;
  autoCodeLength: number;
  autoCodeValue: string | null;
  // File
  fileTableId: string | null;
  // Summary
  summaryTypeId: string | null;
  // After SP
  afterSPObjectId: string | null;
  // SetRange (cascading lookup filter)
  setRangeSourceFieldName: string | null;
  setRangeTargetFieldName: string | null;
  // Misc
  label: boolean;
  preview: boolean;
  visibleInEditForm: boolean;
  /** Eski maske/regex kolonu (MaskEdit); yeni alan `mask` */
  regex: string | null;
  /** Literal (9/A/#...) veya regex maske — web MaskFormatHelper.GetEffectiveMaskRegex */
  mask?: string | null;
  /** Maske/regex'e uymayan değerde gösterilecek mesaj (Sys_ObjectFields.MaskErrorMessage, #495) */
  maskErrorMessage?: string | null;
  /** Alanın formdaki görünümü (JSON, #489) — bkz. features/editform/layout/formSettings. */
  editFormStyle?: string | null;
  editFormColumnSpan: number;
  /** Lookup çoklu seçim (MultiSelect + metin tip) — değerler ';' ile birleşir */
  multiSelect?: boolean;
  /** Alanın üzerine gelindiğinde gösterilen açıklama (Sys_ObjectFields.Tooltip) */
  tooltip?: string | null;
  /** Otomatik kodun son üretilen değeri kullanıcı tarafından mı verildi */
  autoCodeValueExplicit?: boolean | null;
  /** Bağlı kayda gitme butonu */
  linkDataSourceObjectId?: string | null;
  linkFieldName?: string | null;
  /** Satır kopyalamada hariç */
  excludedFromCopy?: boolean;
  /** Grid açılışında filtre panelinde parametre olarak listelenir (SP nesnelerinde göz ardı edilir) */
  isFilterParam?: boolean;
}

export interface SystemEditorType {
  editorTypeId: string;
  editorTypeName: string;
  // Text, Check, Lookup, Date, DateTime, Spin, Image, RichText, Memo, File, Mask, Url
}

export interface SystemRoleObject {
  roleObjectId: string;
  roleId: string;
  objectId: string;
  readOnly: boolean;
  canInsert: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canExport: boolean;
  canImport: boolean;
  maxRecords: number | null;
}

export interface SystemRoleObjectField {
  roleObjectFieldId: string;
  roleObjectId: string;
  objectFieldId: string;
  /** Grid kolonu görünürlüğü */
  visible: boolean;
  readOnly: boolean;
  /** Edit form'da görünürlük (web IsEditFormVisible bunu okur) */
  editFormVisible?: boolean;
  columnChooserVisible?: boolean;
  defaultValue: string | null;
}

/** Sys_NodeActionTypes — barkod okutma "İşlem Tipi" (giriş/çıkış vb.) */
export interface SystemNodeActionType {
  nodeActionTypeId: string;
  objectId: string;
  actionName: string;
  displayOrder: number;
}

export interface SystemIcon {
  iconId: string;
  iconCode: string | null;
  /** PNG, base64 (byte[] JSON serileştirmesi) */
  icon: string | null;
}

export interface UserScreenNode {
  screenId: string;
  screenUserId: string;
  screenName: string;
  caption: string;
  iconName: string | null;
  /** Tb_Rn_Sys_Screens.IconId → Icons ucundan PNG */
  iconId: string | null;
  parentScreenId: string | null;
  objectId: string | null;
  screenTypeName: string | null;
  displayOrder: number;
  favorite: boolean;
  /**
   * Kullanıcının bu ekrana yetkisi var mı? `false` olanlar yalnızca gezinmek için duran
   * üst modüller: alt ekranlardan birine yetki varsa o ekranın üstündeki modüller de
   * çizilir (web'in davranışı). Tıklanamaz ve favorilenemezler.
   */
  accessible: boolean;
  children: UserScreenNode[];
}

export interface SystemObjectDetail {
  objectDetailId: string;
  objectId: string; // Master object
  detailDataSourceObjectId: string; // Detail object
  detailObjectAlias: string; // Tab label (e.g. "Hatalar", "Siparişler")
  displayOrder: number;
  sourceFieldName: string; // Master PK field
  targetFieldName: string; // Detail FK field
  splitterPercentage: number;
}

export interface SystemObjectFilter {
  objectFilterId: string;
  objectId: string;
  objectFilterName: string;
  whereClause: string | null;
  default: boolean;
  displayOrder: number;
}

export type EditorTypeName =
  | 'Text'
  | 'Check'
  | 'Lookup'
  | 'Time'
  | 'Date'
  | 'DateTime'
  | 'Spin'
  | 'Image'
  | 'RichText'
  | 'Memo'
  | 'File'
  | 'Mask'
  | 'Url';

// Object Actions (Veri İşlemleri)
export interface SystemObjectAction {
  objectActionId: string;
  objectId: string;
  actionTypeId: string;
  targetObjectId: string | null;
  displayOrder: number;
  actionName: string;
  beginGroup: boolean | null;
  /** Hedef nesne pencere yerine doğrudan uygulama sekmesinde açılır (#445). */
  showInTab?: boolean | null;
  resultDataSourceObjectId: string | null;
  sourceFilterFieldName: string | null;
  targetFilterFieldName: string | null;
  groupName: string | null;
  approved: boolean | null;
  resultDataSourceWhereClause: string | null;
  multiSelect: boolean | null;
  toolbarButton: boolean | null;
  styleId: string | null;
  confirmationMessage: string | null;
  iconName: string | null;
  /**
   * İşlem bittikten sonra ekranın TÜM verisi yeniden çekilsin mi (OctoLab: "Data Source
   * Requery"). `false` ise yalnızca işlemin dokunduğu satırlar tazelenir.
   *
   * Veritabanı varsayılanı 1: ölçümde 712 işlemin 697'si true, yalnızca 11'i false.
   * Yani dar yenileme, açıkça kapatılmış işlemlere özgü bir hızlandırma.
   */
  dataSourceRequery?: boolean | null;
}

export interface SystemRoleObjectAction {
  roleObjectActionId: string;
  objectActionId: string;
  roleId: string;
  visible: boolean;
}

// ObjectTypeId constants (derived from "Table".ToMD5Guid(), "View".ToMD5Guid(), "Stored Procedure".ToMD5Guid())
export const OBJECT_TYPE_IDS = {
  TABLE: '795bc451-5d5d-a318-e4e0-c37e8b20a141',
  VIEW: 'ebcf5143-b6e4-8a1d-a5ef-a1d020710005',
  STORED_PROCEDURE: '01a8b67a-f4f7-74cb-4956-2e49897e8551',
} as const;

// ActionType constant IDs (matches web SystemActionType enum — "Name".ToMD5Guid())
export const ACTION_TYPE_IDS = {
  ACTION: 'c9f64b00-00a4-1403-0292-e97330236c53',
  REPORT_DESIGNER: 'cb0fea1f-2f1e-be88-bc24-727fd1a913e5',
  SURVEY_DESIGNER: 'a462a6ec-bd2a-e812-ea75-e6646377471b',
  SURVEY: 'ccf9d99f-30d6-4bcd-6894-051c35710572',
} as const;

// Survey types
export interface SystemSurvey {
  surveyId: string;
  title: string;
  description: string | null;
  sections: SurveySection[];
}

export interface SurveySection {
  sectionID: number;
  sectionTitle: string;
  description: string | null;
  questions: SurveyQuestion[];
  /** Bölüm yalnız bu koşul sağlanınca gösterilir; boşsa her zaman. */
  visibleIf?: SurveyCondition | null;
}

export interface SurveyQuestion {
  questionID: number;
  text: string;
  base64Images: string[] | null;
  type: SurveyQuestionType | number;
  isRequired: boolean;
  placeholder: string | null;
  options: SurveyOption[];
  // Form tasarımcısı ayarları — hepsi isteğe bağlı; eski anketlerde gelmez.
  /** Soru metninin altındaki yardım metni. */
  description?: string | null;
  /** Satırdaki genişlik; boş = Full. */
  width?: SurveyQuestionWidth | null;
  /** Alt sınır: metinde karakter, sayıda değer, çoklu seçimde seçim adedi. */
  min?: number | null;
  /** Üst sınır; anlamı `min` ile aynı. */
  max?: number | null;
  /** Tek/çoklu seçimde seçenek sütunu (1-4). */
  optionColumns?: number | null;
  /** Soru yalnız bu koşul sağlanınca gösterilir; boşsa her zaman. */
  visibleIf?: SurveyCondition | null;
}

export type SurveyQuestionWidth = 'Full' | 'Half' | 'Third';

/**
 * Görünürlük koşulu: kaynak soruda `optionIDs`'ten biri seçiliyse doğru; liste boşsa kaynak
 * sorunun cevaplanmış olması yeter. Kaynak (bölüm, soru) kimlik çiftiyle gösterilir — yanıtlar
 * da bu çiftle eşleşir.
 */
export interface SurveyCondition {
  sectionID: number;
  questionID: number;
  optionIDs: number[];
}

export interface SurveyOption {
  optionID: number;
  text: string;
  base64Image: string | null;
  isOtherOption: boolean;
  isImageRequired: boolean;
  isDescriptionRequired: boolean;
}

export type SurveyQuestionType =
  'ShortText' | 'LongText' | 'Number' | 'Checkbox' | 'RadioButton' | 'Dropdown' | 'Date' | 'Time';

/** C# enum sırasıyla soru tipleri (indeks = enum değeri). */
export const SURVEY_QUESTION_TYPES: readonly SurveyQuestionType[] = [
  'ShortText', 'LongText', 'Number', 'Checkbox', 'RadioButton', 'Dropdown', 'Date', 'Time',
];

/**
 * C# SurveyQuestionType enum → integer mapping.
 * API returns integers when JsonStringEnumConverter is not configured.
 */
const QUESTION_TYPE_MAP: Record<number, SurveyQuestionType> = {
  0: 'ShortText',
  1: 'LongText',
  2: 'Number',
  3: 'Checkbox',
  4: 'RadioButton',
  5: 'Dropdown',
  6: 'Date',
  7: 'Time',
};

/** Normalize question type from API (handles both integer and string values) */
export function normalizeQuestionType(raw: number | string): SurveyQuestionType {
  if (typeof raw === 'number') {
    return QUESTION_TYPE_MAP[raw] ?? 'ShortText';
  }
  return raw as SurveyQuestionType;
}

/** Soru tipini DAL'ın beklediği enum sayısına çevirir (DAL'da JsonStringEnumConverter yok). */
export function questionTypeToInt(type: SurveyQuestionType | number): number {
  if (typeof type === 'number') return type;
  const i = SURVEY_QUESTION_TYPES.indexOf(type);
  return i < 0 ? 0 : i;
}

/**
 * Seçenek görseli: Blazor tasarımcısı tam data URL ("data:image/png;base64,…") yazar,
 * mobil ham base64 bekler — ikisini de gösterilebilir src'ye çevirir.
 */
export function surveyImageSrc(value: string): string {
  return value.startsWith('data:') ? value : `data:image/png;base64,${value}`;
}
